import { useState, useEffect } from "react"
import { Label } from "@/components/ui/label"
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useQuery } from "@tanstack/react-query"
import { useAuth } from "@/contexts/auth"

interface OvertimeReason {
  id: string
  code: string
  main_title: string
  reason: string
  date_created: string
}

interface JustificationInputProps {
  justification: string
  onJustificationChange: (justification: string) => void
}

export function JustificationInput({
  onJustificationChange
}: JustificationInputProps) {
  const [overtimeReasons, setOvertimeReasons] = useState<OvertimeReason[]>([])
  const [mainTitles, setMainTitles] = useState<string[]>([])
  const [selectedMainTitle, setSelectedMainTitle] = useState<string>("")
  const [availableReasons, setAvailableReasons] = useState<OvertimeReason[]>([])
  const [selectedReasonId, setSelectedReasonId] = useState<string>("")
  
  const { user } = useAuth()
  
  // Use React Query: this will hit cache instantly if prefetched on login
  const { data: prefetchedReasons = [], isLoading: reasonsLoading } = useQuery({
    queryKey: ['overtime_reasons', user?.companyId],
    queryFn: async () => {
      if (!user?.companyId) {
        return [] as OvertimeReason[]
      }

      const { data, error } = await supabase
        .from('overtime_reasons')
        .select('*')
        .eq('company_id', user.companyId)
        .eq('is_active', true)
        .order('main_title', { ascending: true })
        .order('reason', { ascending: true })
      if (error) {
        toast.error('Failed to load overtime reasons')
        return [] as OvertimeReason[]
      }
      return data || []
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!user?.companyId,
  })

  // Sync local state when prefetched reasons are available
  useEffect(() => {
    setOvertimeReasons(prefetchedReasons)
  }, [prefetchedReasons])

  // Derive unique main titles from reasons
  useEffect(() => {
    const uniqueMainTitles = [...new Set(overtimeReasons.map(reason => reason.main_title))]
    setMainTitles(uniqueMainTitles)
  }, [overtimeReasons])

  // Update available reasons when main title changes
  useEffect(() => {
    if (selectedMainTitle) {
      const filtered = overtimeReasons.filter(reason => reason.main_title === selectedMainTitle)
      setAvailableReasons(filtered)
      setSelectedReasonId("") // Reset reason selection
    } else {
      setAvailableReasons([])
      setSelectedReasonId("")
    }
  }, [selectedMainTitle, overtimeReasons])

  // Handle main title selection
  const handleMainTitleChange = (value: string) => {
    setSelectedMainTitle(value)
    setSelectedReasonId("")
  }

  // Handle specific reason selection
  const handleReasonChange = (value: string) => {
    setSelectedReasonId(value)
    // Update the parent component with the selected reason ID
    onJustificationChange(value)
  }

  if (reasonsLoading) {
    return (
      <div className="space-y-2">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Category + Specific Reason in one row */}
      <div className="grid grid-cols-12 gap-4">
        {/* Main Category Selection */}
        <div className="col-span-12 sm:col-span-6 space-y-2">
          <Label className="text-xs text-muted-foreground">Justification - Category</Label>
          <Select value={selectedMainTitle} onValueChange={handleMainTitleChange}>
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select reason category..." />
            </SelectTrigger>
            <SelectContent className="max-h-[250px] overflow-y-auto">
              {mainTitles.map((title) => (
                <SelectItem key={title} value={title} className="py-2.5">
                  {title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Specific Reason Selection */}
        {selectedMainTitle && (
          <div className="col-span-12 sm:col-span-6 space-y-2">
            <Label className="text-xs text-muted-foreground">Justification - Specific reason</Label>
            <Select value={selectedReasonId} onValueChange={handleReasonChange}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Select specific reason..." />
              </SelectTrigger>
              <SelectContent className="max-h-[250px] overflow-y-auto">
                {availableReasons.map((reason) => (
                  <SelectItem key={reason.id} value={reason.id} className="py-2.5">
                    {reason.reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  )
}
